import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, Input, ViewEncapsulation, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { SharedModule } from 'primeng/api';
import { BaseComponent, PARENT_INSTANCE } from 'primeng/basecomponent';
import * as i1 from 'primeng/bind';
import { Bind } from 'primeng/bind';
import { style } from '@primeuix/styles/progressspinner';
import { BaseStyle } from 'primeng/base';
const classes = {
  root: () => ['p-progressspinner'],
  spin: 'p-progressspinner-spin',
  circle: 'p-progressspinner-circle'
};
let ProgressSpinnerStyle = /*#__PURE__*/(() => {
  class ProgressSpinnerStyle extends BaseStyle {
    name = 'progressspinner';
    style = style;
    classes = classes;
    static ɵfac = /* @__PURE__ */(() => {
      let ɵProgressSpinnerStyle_BaseFactory;
      return function ProgressSpinnerStyle_Factory(__ngFactoryType__) {
        return (ɵProgressSpinnerStyle_BaseFactory || (ɵProgressSpinnerStyle_BaseFactory = i0.ɵɵgetInheritedFactory(ProgressSpinnerStyle)))(__ngFactoryType__ || ProgressSpinnerStyle);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ProgressSpinnerStyle,
      factory: ProgressSpinnerStyle.ɵfac
    });
  }
  return ProgressSpinnerStyle;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 *
 * ProgressSpinner is a process status indicator.
 *
 * [Live Demo](https://www.primeng.org/progressspinner)
 *
 * @module progressspinnerstyle
 *
 */
var ProgressSpinnerClasses = /*#__PURE__*/function (ProgressSpinnerClasses) {
  /**
   * Class name of the root element
   */
  ProgressSpinnerClasses["root"] = "p-progressspinner";
  /**
   * Class name of the spin element
   */
  ProgressSpinnerClasses["spin"] = "p-progressspinner-spin";
  /**
   * Class name of the circle element
   */
  ProgressSpinnerClasses["circle"] = "p-progressspinner-circle";
  return ProgressSpinnerClasses;
}(ProgressSpinnerClasses || {});
const PROGRESSSPINNER_INSTANCE = /*#__PURE__*/new InjectionToken('PROGRESSSPINNER_INSTANCE');
/**
 * ProgressSpinner is a process status indicator.
 * @group Components
 */
let ProgressSpinner = /*#__PURE__*/(() => {
  class ProgressSpinner extends BaseComponent {
    componentName = 'ProgressSpinner';
    $pcProgressSpinner = inject(PROGRESSSPINNER_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    bindDirectiveInstance = inject(Bind, {
      self: true
    });
    /**
     * Class of the element.
     * @deprecated since v20.0.0, use `class` instead.
     * @group Props
     */
    styleClass;
    /**
     * Width of the circle stroke.
     * @group Props
     */
    strokeWidth = '2';
    /**
     * Color for the background of the circle.
     * @group Props
     */
    fill = 'none';
    /**
     * Duration of the rotate animation.
     * @group Props
     */
    animationDuration = '2s';
    /**
     * Used to define a aria label attribute the current element.
     * @group Props
     */
    ariaLabel;
    onAfterViewChecked() {
      this.bindDirectiveInstance.setAttrs(this.ptms(['host', 'root']));
    }
    _componentStyle = inject(ProgressSpinnerStyle);
    static ɵfac = /* @__PURE__ */(() => {
      let ɵProgressSpinner_BaseFactory;
      return function ProgressSpinner_Factory(__ngFactoryType__) {
        return (ɵProgressSpinner_BaseFactory || (ɵProgressSpinner_BaseFactory = i0.ɵɵgetInheritedFactory(ProgressSpinner)))(__ngFactoryType__ || ProgressSpinner);
      };
    })();
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: ProgressSpinner,
      selectors: [["p-progressSpinner"], ["p-progress-spinner"], ["p-progressspinner"]],
      hostVars: 5,
      hostBindings: function ProgressSpinner_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-label", ctx.ariaLabel)("role", "progressbar")("aria-busy", true);
          i0.ɵɵclassMap(ctx.cn(ctx.cx("root"), ctx.styleClass));
        }
      },
      inputs: {
        styleClass: "styleClass",
        strokeWidth: "strokeWidth",
        fill: "fill",
        animationDuration: "animationDuration",
        ariaLabel: "ariaLabel"
      },
      features: [i0.ɵɵProvidersFeature([ProgressSpinnerStyle, {
        provide: PROGRESSSPINNER_INSTANCE,
        useExisting: ProgressSpinner
      }, {
        provide: PARENT_INSTANCE,
        useExisting: ProgressSpinner
      }]), i0.ɵɵHostDirectivesFeature([i1.Bind]), i0.ɵɵInheritDefinitionFeature],
      decls: 2,
      vars: 10,
      consts: [["viewBox", "25 25 50 50", 3, "pBind"], ["cx", "50", "cy", "50", "r", "20", "stroke-miterlimit", "10", 3, "pBind"]],
      template: function ProgressSpinner_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵnamespaceSVG();
          i0.ɵɵelementStart(0, "svg", 0);
          i0.ɵɵelement(1, "circle", 1);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.cx("spin"));
          i0.ɵɵstyleProp("animation-duration", ctx.animationDuration);
          i0.ɵɵproperty("pBind", ctx.ptm("spin"));
          i0.ɵɵadvance();
          i0.ɵɵclassMap(ctx.cx("circle"));
          i0.ɵɵproperty("pBind", ctx.ptm("circle"));
          i0.ɵɵattribute("fill", ctx.fill)("stroke-width", ctx.strokeWidth);
        }
      },
      dependencies: [CommonModule, SharedModule, Bind],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return ProgressSpinner;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProgressSpinnerModule = /*#__PURE__*/(() => {
  class ProgressSpinnerModule {
    static ɵfac = function ProgressSpinnerModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ProgressSpinnerModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: ProgressSpinnerModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [ProgressSpinner, SharedModule, SharedModule]
    });
  }
  return ProgressSpinnerModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { ProgressSpinner, ProgressSpinnerClasses, ProgressSpinnerModule, ProgressSpinnerStyle };
